/** Create / edit dialog for a notification theme (react-hook-form + zod).
 *
 *  A theme styles deliveries: `email_shell` (a DTL document wrapping the
 *  rendered email body — must contain the `{{ body }}` insertion point when
 *  non-empty), `tokens` (free-form design tokens exposed to templates as
 *  `theme.*`, deep-merged over the operator color theme), `inline_css`
 *  (premailer at send time), and per-channel text signatures appended to
 *  sms/chat/whatsapp/airbnb bodies. `tenant` is clearable: cleared = a
 *  system-wide theme (`tour_operator: null`). Tokens are edited as a JSON
 *  textarea validated on blur (must parse to an object).
 *  Notification_Theme has no optimistic-concurrency token, so writes are
 *  last-writer-wins. */
import { zodResolver } from '@hookform/resolvers/zod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createTheme,
  update as updateTheme,
  type NotificationThemeDetail,
  type NotificationThemeWrite,
} from '@/api/notificationThemes';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';

import { THEME_HELP } from './fieldHelp';

/** Mirror of the backend's THEME_BODY_SLOT_RE insertion-point check. */
const BODY_SLOT_RE = /\{\{\s*body\s*\}\}/;

const MONO_INPUT_STYLE = { fontFamily: 'monospace', fontSize: 13 } as const;

const FormSchema = z.object({
  codename: z
    .string()
    .trim()
    .min(1, 'Codename is required')
    .max(64)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      'Letters, digits and underscore only; cannot start with a digit.',
    ),
  name: z.string().max(256),
  description: z.string(),
  enabled: z.boolean(),
  email_shell: z.string().refine((v) => v.trim() === '' || BODY_SLOT_RE.test(v), {
    message: 'A non-empty email shell must contain the {{ body }} insertion point.',
  }),
  inline_css: z.boolean(),
  sms_signature: z.string(),
  chat_signature: z.string(),
  whatsapp_signature: z.string(),
  airbnb_signature: z.string(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  codename: '',
  name: '',
  description: '',
  enabled: true,
  email_shell: '',
  inline_css: false,
  sms_signature: '',
  chat_signature: '',
  whatsapp_signature: '',
  airbnb_signature: '',
};

function toDefaults(t: NotificationThemeDetail | null): FormValues {
  if (!t) return EMPTY;
  return {
    codename: t.codename,
    name: t.name ?? '',
    description: t.description ?? '',
    enabled: t.enabled,
    email_shell: t.email_shell ?? '',
    inline_css: t.inline_css ?? false,
    sms_signature: t.sms_signature ?? '',
    chat_signature: t.chat_signature ?? '',
    whatsapp_signature: t.whatsapp_signature ?? '',
    airbnb_signature: t.airbnb_signature ?? '',
  };
}

function toTokensText(t: NotificationThemeDetail | null): string {
  const tokens = t?.tokens ?? {};
  return Object.keys(tokens).length ? JSON.stringify(tokens, null, 2) : '';
}

/** Parse the tokens textarea: '' → {}, else must be a JSON object. Returns
 *  null when invalid (the caller shows `tokensError`). */
function parseTokens(text: string): Record<string, unknown> | null {
  if (text.trim() === '') return {};
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

/** The four per-channel signature fields, in display order. */
const SIGNATURE_FIELDS: {
  field: 'sms_signature' | 'chat_signature' | 'whatsapp_signature' | 'airbnb_signature';
  label: string;
  channel: string;
}[] = [
  { field: 'sms_signature', label: 'SMS signature', channel: 'SMS' },
  { field: 'chat_signature', label: 'Chat signature', channel: 'chat' },
  { field: 'whatsapp_signature', label: 'WhatsApp signature', channel: 'WhatsApp' },
  { field: 'airbnb_signature', label: 'Airbnb signature', channel: 'Airbnb' },
];

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  theme: NotificationThemeDetail | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function ThemeFormDialog({ open, theme, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = theme !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.notification_theme',
    objectPk: theme?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['notification_themes'] }),
  });

  // Tenant + the tokens JSON textarea live outside RHF (self-contained
  // controlled inputs, like the block editor). null tenant = system-wide.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [tokensText, setTokensText] = useState('');
  const [tokensError, setTokensError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(theme),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(toDefaults(theme));
    setTokensText(toTokensText(theme));
    setTokensError(null);
    if (theme) {
      setTenant(
        theme.tenant
          ? { id: theme.tenant.id, name: theme.tenant.name, code: theme.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, theme?.id]);

  const tenantId = tenant?.id ?? null;

  const validateTokens = () => {
    const parsed = parseTokens(tokensText);
    setTokensError(
      parsed === null ? 'Must be a JSON object, e.g. {"color": {"primary": "#0a1a3f"}}.' : null,
    );
    return parsed;
  };

  const mutation = useMutation({
    mutationFn: (body: NotificationThemeWrite) =>
      isEdit ? updateTheme(theme.id, body) : createTheme(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Theme updated.' : 'Theme created.', 'success');
      qc.invalidateQueries({ queryKey: ['notification_themes'] });
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    const tokens = validateTokens();
    if (tokens === null) return;
    mutation.mutate({
      tour_operator: tenantId,
      codename: v.codename.trim(),
      name: v.name.trim(),
      description: v.description.trim(),
      enabled: v.enabled,
      email_shell: v.email_shell,
      tokens,
      inline_css: v.inline_css,
      sms_signature: v.sms_signature,
      chat_signature: v.chat_signature,
      whatsapp_signature: v.whatsapp_signature,
      airbnb_signature: v.airbnb_signature,
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={
        <>
          {isEdit ? `Edit theme · ${theme.codename}` : 'New theme'}{' '}
          <HelpLink story="notification-themes" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create theme'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            {...register('codename')}
            label="Codename"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.codename)}
            helperText={
              errors.codename?.message ??
              'Referenced by the operator config notifications.default_theme value.'
            }
            InputProps={{ endAdornment: <FieldHelp help={THEME_HELP.codename} /> }}
          />
          <TextField
            {...register('name')}
            label="Name"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.name)}
            helperText={errors.name?.message ?? 'Optional human-readable label.'}
          />
        </Box>

        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message ?? 'Optional.'}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <TenantPicker
              value={tenant}
              featured={isEdit ? undefined : pageTenants}
              onChange={setTenant}
              label="Operator"
              placeholder="System / global (no operator)"
            />
          </Box>
          <FieldHelp help={THEME_HELP.operator} />
        </Box>

        <Divider />

        <TextField
          {...register('email_shell')}
          label="Email shell"
          size="small"
          fullWidth
          multiline
          minRows={6}
          slotProps={{ htmlInput: { style: MONO_INPUT_STYLE } }}
          error={Boolean(errors.email_shell)}
          helperText={
            errors.email_shell?.message ??
            'DTL document wrapping the rendered email body — must contain the {{ body }} insertion point. Empty = no wrapping.'
          }
          InputProps={{ endAdornment: <FieldHelp help={THEME_HELP.emailShell} /> }}
        />

        <TextField
          label="Design tokens (JSON)"
          size="small"
          fullWidth
          multiline
          minRows={4}
          value={tokensText}
          onChange={(e) => setTokensText(e.target.value)}
          onBlur={validateTokens}
          slotProps={{ htmlInput: { style: MONO_INPUT_STYLE } }}
          error={tokensError !== null}
          helperText={
            tokensError ??
            'JSON object merged over the operator colors; exposed to templates as {{ theme.* }}.'
          }
          InputProps={{ endAdornment: <FieldHelp help={THEME_HELP.tokens} /> }}
        />

        <Controller
          control={control}
          name="inline_css"
          render={({ field }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={labelWithHelp(
                'Inline CSS (premailer over the wrapped email body at send time)',
                THEME_HELP.inlineCss,
              )}
            />
          )}
        />

        <Divider />

        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography variant="subtitle2">Text-channel signatures</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack spacing={2}>
              {SIGNATURE_FIELDS.map((s) => (
                <TextField
                  key={s.field}
                  {...register(s.field)}
                  label={s.label}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  error={Boolean(errors[s.field])}
                  helperText={
                    errors[s.field]?.message ??
                    `DTL snippet appended to ${s.channel} bodies at delivery.`
                  }
                  InputProps={{ endAdornment: <FieldHelp help={THEME_HELP.signatures} /> }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider />

        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={labelWithHelp('Enabled', THEME_HELP.enabled)}
            />
          )}
        />
      </Box>
      )}
    </AppDialog>
  );
}
